import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import ColorPicker from '../components/ColorPicker';
import {
  TASK_SUBTHEMES, TASK_CATEGORIES, REFERENCE_LABEL, REFERENCE_PLACEHOLDER,
  REFERENCE_REQUIRED, isGrouped,
} from '../constants';

function renderSubthemeOptions(subthemes) {
  if (!subthemes) return null;
  if (isGrouped(subthemes)) {
    return subthemes.groups.map(g => (
      <optgroup key={g.label} label={g.label}>
        {g.items.map(s => <option key={s} value={s}>{s}</option>)}
      </optgroup>
    ));
  }
  return subthemes.map(s => <option key={s} value={s}>{s}</option>);
}

export default function TaskDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const isPopup = new URLSearchParams(window.location.search).get('popup') === '1';

  const [children, setChildren] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saveStatus, setSaveStatus] = useState('');
  const [form, setForm] = useState({
    title: '', category: 'perso', subtheme: '', reference: '', child_id: '',
    priority: 'normal', due_date: '', due_time: '', recurring: 'none', status: 'a_faire', color: '',
  });

  useEffect(() => {
    api.get('/children').then(r => setChildren(r.data)).catch(() => {});
    api.get('/tasks/meta/dossiers').then(r => setDossiers(r.data)).catch(() => {});

    if (!isNew) {
      api.get('/tasks', { params: {} }).then(r => {
        const task = r.data.find(t => t.id === parseInt(id));
        if (task) {
          setForm({
            title: task.title,
            category: task.category,
            subtheme: task.subtheme || '',
            reference: task.reference || '',
            child_id: task.child_id || '',
            priority: task.priority,
            due_date: task.due_date ? task.due_date.split('T')[0] : '',
            due_time: task.due_time || '',
            recurring: task.recurring,
            status: task.status,
            color: task.color || '',
          });
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [id, isNew]);

  const save = async (e) => {
    e.preventDefault();
    setSaveStatus('Enregistrement...');
    const payload = {
      ...form,
      child_id: form.child_id || null,
      subtheme: form.subtheme || null,
      reference: form.reference || null,
      due_date: form.due_date || null,
      due_time: form.due_time || null,
      color: form.color || null,
    };
    try {
      if (isNew) {
        const r = await api.post('/tasks', payload);
        setSaveStatus('✓ Créée');
        if (isPopup) {
          // Dans popup : on remplace l'URL par /taches/:id pour continuer l'édition
          window.history.replaceState({}, '', `/taches/${r.data.id}?popup=1`);
        } else {
          navigate(`/taches/${r.data.id}`);
        }
      } else {
        await api.put(`/tasks/${id}`, payload);
        setSaveStatus('✓ Enregistré');
      }
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (err) {
      setSaveStatus('❌ Erreur');
    }
  };

  const deleteTask = async () => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    await api.delete(`/tasks/${id}`);
    if (isPopup) window.close();
    else navigate('/taches');
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-light)' }}>Chargement...</div>;

  const currentSubthemes = TASK_SUBTHEMES[form.category];
  const useChildDropdown = form.category === 'enfants';
  const refLabel = REFERENCE_LABEL[form.category] || 'Référence';
  const refPlaceholder = REFERENCE_PLACEHOLDER[form.category] || '';
  const refRequired = REFERENCE_REQUIRED[form.category] || false;

  return (
    <div className={isPopup ? 'popup-layout' : ''}>
      <div className="page-header page-header-row">
        <div>
          <h1>{isNew ? 'Nouvelle tâche' : 'Modifier la tâche'}</h1>
          {saveStatus && <p style={{ color: saveStatus.includes('❌') ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>{saveStatus}</p>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isPopup && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/taches')}>← Retour aux tâches</button>
          )}
          {isPopup && (
            <button className="btn btn-secondary btn-sm" onClick={() => window.close()}>Fermer la fenêtre</button>
          )}
        </div>
      </div>

      <div className="card" style={{ maxWidth: 700 }}>
        <form onSubmit={save}>
          <div className="form-group">
            <label>Titre / Description</label>
            <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex : Rédiger conclusions, RDV pédiatre..." required autoFocus />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Catégorie</label>
              <select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value, subtheme: '', reference: '', child_id: '' })}>
                {TASK_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Sous-thème</label>
              <select className="form-input" value={form.subtheme} onChange={e => setForm({ ...form, subtheme: e.target.value })}>
                <option value="">—</option>
                {renderSubthemeOptions(currentSubthemes)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>{refLabel}{refRequired ? ' *' : ''}</label>
            {useChildDropdown ? (
              <select className="form-input" value={form.child_id} onChange={e => setForm({ ...form, child_id: e.target.value })}>
                <option value="">— Choisir un enfant —</option>
                {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value="all">Tous / Fratrie</option>
              </select>
            ) : (
              <input
                className="form-input"
                value={form.reference}
                onChange={e => setForm({ ...form, reference: e.target.value })}
                placeholder={refPlaceholder}
                list={form.category === 'travail' ? 'dossiers-list-detail' : undefined}
                required={refRequired}
              />
            )}
            {form.category === 'travail' && (
              <datalist id="dossiers-list-detail">
                {dossiers.map(d => <option key={d} value={d} />)}
              </datalist>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Priorité</label>
              <select className="form-input" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                <option value="urgent">Urgent</option>
                <option value="important">Important</option>
                <option value="normal">Normal</option>
              </select>
            </div>
            <div className="form-group">
              <label>Statut</label>
              <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value="a_faire">À faire</option>
                <option value="en_cours">En cours</option>
                <option value="fait">Fait</option>
              </select>
            </div>
            <div className="form-group">
              <label>Récurrence</label>
              <select className="form-input" value={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.value })}>
                <option value="none">Unique</option>
                <option value="daily">Chaque jour</option>
                <option value="weekly">Chaque semaine</option>
                <option value="monthly">Chaque mois</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date d'échéance</label>
              <input className="form-input" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Heure</label>
              <input className="form-input" type="time" value={form.due_time} onChange={e => setForm({ ...form, due_time: e.target.value })} />
            </div>
          </div>

          <ColorPicker value={form.color} onChange={c => setForm({ ...form, color: c })} label="Couleur de la tâche" />

          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', marginTop: 20, flexWrap: 'wrap' }}>
            {!isNew && (
              <button type="button" className="btn btn-danger btn-sm" onClick={deleteTask}>🗑️ Supprimer</button>
            )}
            <button type="submit" className="btn btn-primary" style={{ marginLeft: 'auto' }}>
              {isNew ? 'Créer' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
